import type { ReactNode } from 'react';
import { SortArrowsIcon } from '../icons';
import { useTranslation } from '@hooks/useTranslation';

export interface PreviewTableColumn<T> {
  key: string;
  header: string;
  render: (item: T) => ReactNode;
  /** Grid column width, e.g. "3fr" or "1fr". Defaults to "1fr". */
  width?: string;
  /** When true the header is rendered as a clickable button that toggles
   * the parent's sort state via `onSortChange`. */
  sortable?: boolean;
}

export interface PreviewTableSort {
  key: string;
  direction: 'asc' | 'desc';
}

interface PreviewTableProps<T> {
  columns: PreviewTableColumn<T>[];
  data: T[];
  getRowKey: (item: T) => string | number;
  /**
   * Rendered below the header row when `data` is empty. When omitted,
   * an empty data array renders just the header row (existing behavior).
   * Match the DataTable empty-state convention: centred icon + label.
   */
  emptyState?: ReactNode;
  /** Active sort, or null when nothing is sorted. */
  sort?: PreviewTableSort | null;
  /** Called with the clicked column key. Parent decides the next state
   * (typical cycle: none → asc → desc → none). */
  onSortChange?: (key: string) => void;
  /** Extra classes applied to each body row — e.g. a fixed row height. */
  rowClassName?: string;
  /**
   * Minimum total width for the header + rows (e.g. "1393px"). When set, the
   * table scrolls horizontally instead of clipping: rows won't shrink below
   * this width, so fixed-width columns stay intact on narrow screens while
   * still filling wider ones. Omit for the default fluid, non-scrolling table.
   */
  minWidth?: string;
}

export function PreviewTable<T>({
  columns,
  data,
  getRowKey,
  emptyState,
  sort,
  onSortChange,
  rowClassName,
  minWidth,
}: PreviewTableProps<T>) {
  const gridTemplate = columns.map((col) => col.width ?? '1fr').join(' ');
  const gridClass = 'grid items-center gap-8 px-6';
  const isEmpty = data.length === 0;

  return (
    <div
      className={`${minWidth ? 'overflow-x-auto' : 'overflow-hidden'} rounded-input border border-dinnerplate-white bg-off-white`}
    >
      {/* Header */}
      <div
        className={`${gridClass} bg-dinnerplate-white-light`}
        style={{ gridTemplateColumns: gridTemplate, minWidth }}
      >
        {columns.map((col) => (
          <HeaderCell
            key={col.key}
            column={col}
            sort={sort}
            onSortChange={onSortChange}
          />
        ))}
      </div>

      {/* Rows or empty state */}
      {isEmpty
        ? emptyState && (
            <div className="border-t border-dinnerplate-white" style={{ minWidth }}>{emptyState}</div>
          )
        : data.map((item) => (
            <div
              key={getRowKey(item)}
              className={`${gridClass} border-t border-dinnerplate-white ${rowClassName ?? ''}`}
              style={{ gridTemplateColumns: gridTemplate, minWidth }}
            >
              {columns.map((col) => (
                <div key={col.key} className="py-4">
                  {col.render(item)}
                </div>
              ))}
            </div>
          ))}
    </div>
  );
}

interface HeaderCellProps<T> {
  column: PreviewTableColumn<T>;
  sort: PreviewTableSort | null | undefined;
  onSortChange?: (key: string) => void;
}

function HeaderCell<T>({ column, sort, onSortChange }: HeaderCellProps<T>) {
  const { t } = useTranslation();
  const baseClasses = 'py-4 font-inter text-sm font-semibold text-grey';

  if (!column.sortable) {
    return <span className={baseClasses}>{column.header}</span>;
  }

  const isActive = sort?.key === column.key;
  const activeDirection = isActive ? sort?.direction ?? null : null;
  const nextLabel = !isActive
    ? t('common.sort.ascending')
    : activeDirection === 'asc'
      ? t('common.sort.descending')
      : t('common.sort.default');

  return (
    <button
      type="button"
      onClick={() => onSortChange?.(column.key)}
      aria-label={`Sort by ${column.header}, ${nextLabel}`}
      className={`${baseClasses} flex w-full items-center justify-between gap-2 text-left cursor-pointer select-none focus:outline-none`}
    >
      <span>{column.header}</span>
      <SortArrowsIcon activeDirection={activeDirection} className="w-2.5 shrink-0" />
    </button>
  );
}
